import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, FileSpreadsheet, FileText, Image as ImageIcon, Folder, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounce } from '@/hooks/useDebounce';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { base64ToFile, callOneDrive, type OneDriveFile, type OneDriveStatus } from '@/lib/microsoftFiles';
import type { DrivePickKind } from './DriveSpreadsheetPicker';

export const FEATURE_META = {
  id: 'onedrive-picker',
  label: 'Choose a File from OneDrive',
  description: 'Browse Recent, My files and Shared with me (or search) in your own OneDrive to pick a spreadsheet, COA/lab report, or photo/video without downloading it first.',
  triggerType: 'dialog' as const,
  personas: ['seller', 'buyer_seller', 'buyer'] as ('seller' | 'buyer_seller' | 'buyer')[],
};

type View = 'recent' | 'my' | 'shared';
type Crumb = { id: string; driveId: string; name: string };
const VIEWS: { value: View; label: string }[] = [
  { value: 'recent', label: 'Recent' },
  { value: 'my', label: 'My files' },
  { value: 'shared', label: 'Shared with me' },
];
const COPY: Record<DrivePickKind, { description: string; noun: string }> = {
  spreadsheet: { description: 'Browse your folders or search. Excel and CSV files are shown.', noun: 'spreadsheets' },
  document: { description: 'Browse your folders or search. PDFs, Word, Excel, CSV and text files are shown (up to 10 MB).', noun: 'documents' },
  media: { description: 'Browse your folders or search. Photos and videos (MP4, WebM, MOV) up to 10 MB are shown.', noun: 'photos or videos' },
};

/** Read-only OneDrive file picker — the Microsoft twin of DriveSpreadsheetPicker. */
export default function OneDrivePicker({ open, onOpenChange, onPicked, kind = 'spreadsheet' }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPicked: (file: File) => void;
  kind?: DrivePickKind;
}) {
  useFeatureRegistration(FEATURE_META);
  const [status, setStatus] = useState<OneDriveStatus | null>(null);
  const [files, setFiles] = useState<OneDriveFile[] | null>(null);
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [picking, setPicking] = useState<string | null>(null);
  const [view, setView] = useState<View>('recent');
  const [path, setPath] = useState<Crumb[]>([]);

  useEffect(() => {
    if (!open) return;
    setStatus(null);
    callOneDrive<OneDriveStatus>({ action: 'status' }).then(setStatus).catch((e) => toast.error('Couldn’t reach OneDrive', { description: (e as Error).message }));
  }, [open]);

  const ready = !!status?.enabled && status.connected && !status.reconnectRequired;
  const folder = path.length ? path[path.length - 1] : undefined;

  const load = useCallback(async () => {
    setFiles(null);
    try {
      const r = await callOneDrive<{ files?: OneDriveFile[]; reconnectRequired?: boolean }>({
        action: 'files', kind, view, folderId: folder?.id, driveId: folder?.driveId, query: debounced || undefined,
      });
      if (r.reconnectRequired) setStatus((s) => (s ? { ...s, reconnectRequired: true } : s));
      setFiles(r.files ?? []);
    } catch (e) {
      toast.error('Couldn’t list your OneDrive files', { description: (e as Error).message });
      setFiles([]);
    }
  }, [debounced, view, folder?.id, folder?.driveId, kind]);
  useEffect(() => { if (open && ready) load(); }, [open, ready, load]);

  const changeView = (v: string) => { setView(v as View); setPath([]); setQuery(''); };

  const pick = async (f: OneDriveFile) => {
    if (f.isFolder) { setQuery(''); setPath((p) => [...p, { id: f.id, driveId: f.driveId, name: f.name }]); return; }
    setPicking(f.id);
    try {
      const r = await callOneDrive<{ name: string; mimeType: string; base64: string }>({ action: 'download', fileId: f.id, driveId: f.driveId, kind });
      onPicked(base64ToFile(r.base64, r.name, r.mimeType));
      onOpenChange(false);
    } catch (e) {
      toast.error('Couldn’t open that file', { description: (e as Error).message });
    } finally {
      setPicking(null);
    }
  };

  const rootLabel = VIEWS.find((v) => v.value === view)!.label;
  const emptyText = debounced ? `No ${COPY[kind].noun} found for “${debounced}”.` : `No folders or ${COPY[kind].noun} here.`;
  const FileIcon = kind === 'document' ? FileText : kind === 'media' ? ImageIcon : FileSpreadsheet;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Choose from OneDrive</DialogTitle>
          <DialogDescription>{COPY[kind].description}</DialogDescription>
        </DialogHeader>
        {!status ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" aria-label="Loading" /></div>
        ) : !status.enabled ? (
          <p className="text-sm text-muted-foreground py-4">Microsoft Files is turned off for your company. Ask a company admin to turn it on in Company Settings.</p>
        ) : !ready ? (
          <div className="py-4 space-y-3">
            <p className="text-sm text-muted-foreground">{status.reconnectRequired ? 'Your OneDrive connection needs a quick refresh.' : 'Connect your OneDrive first — it takes a few seconds.'}</p>
            <Button asChild variant="outline"><Link to="/profile-settings#connections" onClick={() => onOpenChange(false)}>Go to Connections</Link></Button>
          </div>
        ) : (
          <div className="space-y-3 min-w-0">
            <Input placeholder="Search your OneDrive by file name" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search OneDrive files" />
            {!debounced && (
              <>
                <Tabs value={view} onValueChange={changeView}>
                  <TabsList>
                    {VIEWS.map((v) => <TabsTrigger key={v.value} value={v.value} className="text-xs sm:text-sm">{v.label}</TabsTrigger>)}
                  </TabsList>
                </Tabs>
                <nav aria-label="Current location" className="flex items-center flex-wrap gap-1 text-sm text-muted-foreground min-w-0">
                  {path.length ? (
                    <button type="button" className="hover:text-foreground" onClick={() => setPath([])}>{rootLabel}</button>
                  ) : (
                    <span className="text-foreground font-medium">{rootLabel}</span>
                  )}
                  {path.map((c, i) => (
                    <span key={c.id} className="flex items-center gap-1 min-w-0">
                      <ChevronRight className="w-3 h-3 shrink-0" />
                      <button type="button" className={`truncate max-w-[12rem] ${i === path.length - 1 ? 'text-foreground font-medium' : 'hover:text-foreground'}`} onClick={() => setPath(path.slice(0, i + 1))}>{c.name}</button>
                    </span>
                  ))}
                </nav>
              </>
            )}
            <div className="h-[min(20rem,45vh)] min-h-40 overlay-scroll divide-y divide-border rounded-md border border-border">
              {files === null ? (
                <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Loading" /></div>
              ) : files.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">{emptyText}</p>
              ) : files.map((f) => {
                const Icon = f.isFolder ? Folder : FileIcon;
                return (
                  <button key={`${f.driveId}:${f.id}`} type="button" disabled={!!picking} onClick={() => pick(f)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 disabled:opacity-60">
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{f.name}</span>
                      {f.modifiedTime && <span className="block text-xs text-muted-foreground">Edited {new Date(f.modifiedTime).toLocaleDateString()}</span>}
                    </span>
                    {picking === f.id ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-label="Opening" />
                      : f.isFolder && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
